import { useEffect, useRef, useCallback } from 'react';

interface BarcodeScannerOptions {
  onScan: (barcode: string) => void;
  onError?: () => void;
  minLength?: number;
  maxDelay?: number; // max ms between keystrokes to count as scan
  enabled?: boolean;
}

/**
 * Hook to detect barcode scanner input.
 * USB barcode scanners send characters rapidly then press Enter.
 * We detect rapid keystrokes (within maxDelay ms) ending with Enter.
 */
export function useBarcodeScanner({
  onScan,
  onError,
  minLength = 3,
  maxDelay = 50,
  enabled = true,
}: BarcodeScannerOptions) {
  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetBuffer = useCallback(() => {
    bufferRef.current = '';
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input/textarea (unless it's the POS search)
      const target = e.target as HTMLElement;
      const isSearchInput = target.getAttribute('data-barcode-target') === 'true';
      if (
        !isSearchInput &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }

      const now = Date.now();
      const timeSinceLastKey = now - lastKeyTimeRef.current;

      // If too much time passed, reset buffer
      if (timeSinceLastKey > maxDelay && bufferRef.current.length > 0) {
        bufferRef.current = '';
      }

      lastKeyTimeRef.current = now;

      if (e.key === 'Enter') {
        e.preventDefault();
        if (bufferRef.current.length >= minLength) {
          onScan(bufferRef.current);
        }
        bufferRef.current = '';
        return;
      }

      // Only accept printable characters
      if (e.key.length === 1) {
        bufferRef.current += e.key;

        // Clear buffer after timeout (in case Enter never comes)
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          bufferRef.current = '';
        }, 500);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled, onScan, onError, minLength, maxDelay]);

  return { resetBuffer };
}
